import { COMMERCIAL_TERMS_EFFECTIVE_LABEL } from './publicMarketplaceLegal';

export type CommercialTermsBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'heading'; text: string }
  | { kind: 'subheading'; text: string }
  | { kind: 'bullets'; items: readonly string[] }
  | { kind: 'table'; rows: readonly (readonly string[])[] };

export interface CommercialTermsDocument {
  heading: string;
  subtitle: string;
  effectiveVersion: string;
  blocks: readonly CommercialTermsBlock[];
}

interface CommercialTermsRelease {
  effectiveVersion: string;
  publishedAddress: string;
  versionHistory: readonly (readonly string[])[];
}

const buildCommercialTermsOfUseDocument = (
  release: CommercialTermsRelease
): CommercialTermsDocument => ({
  heading: 'Términos y Condiciones de Uso del Sistema',
  subtitle: 'Servicio SaaS multicliente provisto por CRYPTOLOGY S.A.C.',
  effectiveVersion: release.effectiveVersion,
  blocks: [
    {
      kind: 'table',
      rows: [
        [
          'Aviso principal Al iniciar sesión, crear una cuenta, acceder o hacer uso del sistema, el Usuario declara haber leído, entendido y aceptado estos Términos y Condiciones de Uso. Si el Usuario no está de acuerdo, no debe acceder ni utilizar el sistema.',
        ],
      ],
    },
    { kind: 'subheading', text: 'Identificación del proveedor' },
    {
      kind: 'table',
      rows: [
        ['Razón social', 'CRYPTOLOGY S.A.C.'],
        ['RUC', '20604970807'],
        ['Domicilio publicado', release.publishedAddress],
        ['Correo de contacto', 'hola@inmueble.red'],
      ],
    },
    { kind: 'heading', text: '1. Finalidad y aceptación' },
    {
      kind: 'paragraph',
      text: 'Estos Términos regulan el acceso y uso del sistema, plataforma, aplicación, módulos, interfaces y servicios digitales provistos por CRYPTOLOGY S.A.C. bajo modalidad SaaS multicliente.',
    },
    {
      kind: 'paragraph',
      text: 'Estos Términos aplican a toda persona natural que acceda al sistema con una cuenta de usuario, incluyendo administradores, trabajadores, colaboradores, representantes, contratistas, operadores, usuarios técnicos o terceros autorizados por una organización que utilice el servicio.',
    },
    {
      kind: 'paragraph',
      text: 'Al iniciar sesión, crear una cuenta, marcar una casilla de aceptación, hacer clic en un botón de ingreso, acceder a una funcionalidad o continuar usando el sistema, el Usuario declara haber leído, entendido y aceptado estos Términos.',
    },
    {
      kind: 'paragraph',
      text: 'El uso del sistema también puede estar sujeto a acuerdos comerciales, propuestas, órdenes de servicio, políticas internas o anexos suscritos entre CRYPTOLOGY S.A.C. y la organización que otorgó acceso al Usuario. Estos Términos no transfieren propiedad intelectual ni sustituyen obligaciones comerciales específicas pactadas con dicha organización.',
    },
    { kind: 'heading', text: '2. Definiciones' },
    {
      kind: 'paragraph',
      text: 'Cryptology: CRYPTOLOGY S.A.C., proveedor del sistema SaaS y titular de los derechos sobre el software, salvo componentes de terceros identificados conforme a sus propias condiciones.',
    },
    {
      kind: 'paragraph',
      text: 'Sistema: el software, plataforma, aplicación, módulos, interfaces, APIs, paneles, flujos, automatizaciones, documentación funcional, actualizaciones, parches y servicios asociados puestos a disposición por Cryptology.',
    },
    {
      kind: 'paragraph',
      text: 'Usuario: la persona natural que accede o utiliza el Sistema mediante una cuenta autorizada.',
    },
    {
      kind: 'paragraph',
      text: 'Organización: la empresa, entidad, institución, negocio o persona jurídica que habilita o autoriza el acceso del Usuario al Sistema para fines propios de su operación.',
    },
    {
      kind: 'paragraph',
      text: 'Cuenta: credencial, perfil, sesión, rol o acceso asignado a un Usuario para utilizar el Sistema.',
    },
    {
      kind: 'paragraph',
      text: 'Datos de la Organización: información, registros, documentos, archivos, datos comerciales, leads, prospectos, clientes, contactos, oportunidades comerciales, historial de comunicaciones, cotizaciones, tareas, notas, reportes, configuraciones, datos de usuarios, datos de clientes finales y demás contenido ingresado, cargado, generado o procesado mediante el Sistema por cuenta de una Organización.',
    },
    {
      kind: 'paragraph',
      text: 'Datos Personales: información referida a personas naturales identificadas o identificables, conforme a la normativa peruana aplicable sobre protección de datos personales.',
    },
    {
      kind: 'paragraph',
      text: 'Incidente de Seguridad: evento confirmado que compromete de forma no autorizada la confidencialidad, integridad o disponibilidad de Datos de la Organización o Datos Personales procesados mediante el Sistema.',
    },
    {
      kind: 'paragraph',
      text: 'SaaS multicliente: modalidad de servicio en la que el Sistema es operado por Cryptology en infraestructura administrada por Cryptology o sus proveedores tecnológicos, con separación lógica entre organizaciones o espacios de trabajo.',
    },
    { kind: 'heading', text: '3. Alcance de uso permitido' },
    {
      kind: 'paragraph',
      text: 'Cryptology autoriza al Usuario a acceder y usar el Sistema únicamente para las operaciones propias de la Organización que le otorgó acceso, de acuerdo con su rol, permisos, módulos habilitados y políticas internas aplicables.',
    },
    {
      kind: 'paragraph',
      text: 'El Usuario puede utilizar el Sistema para actividades legítimas relacionadas con gestión comercial, operaciones, atención a clientes, comunicaciones, reportes, automatizaciones, administración, seguimiento, soporte, análisis u otros procesos autorizados por la Organización.',
    },
    {
      kind: 'paragraph',
      text: 'Este derecho de uso es limitado, personal al Usuario autorizado, no exclusivo, no transferible, no subautorizable y sujeto al cumplimiento de estos Términos, de las políticas de la Organización y de las condiciones comerciales aplicables al servicio.',
    },
    {
      kind: 'paragraph',
      text: 'El Usuario no podrá usar el Sistema para fines personales no autorizados, fines ajenos a la Organización, fines ilegales, fines fraudulentos, fines competitivos contra Cryptology o fines que excedan el alcance otorgado por la Organización.',
    },
    {
      kind: 'heading',
      text: '4. Servicio SaaS multicliente e infraestructura provista por Cryptology',
    },
    {
      kind: 'paragraph',
      text: 'El Sistema se presta bajo modalidad SaaS multicliente. Esto significa que Cryptology opera, mantiene, actualiza, monitorea y administra la plataforma en infraestructura tecnológica provista o contratada por Cryptology.',
    },
    {
      kind: 'paragraph',
      text: 'El Usuario no recibe copias del software, ejecutables, contenedores, servidores, bases de datos subyacentes, repositorios, claves internas, herramientas de despliegue ni acceso administrativo a la infraestructura de Cryptology.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology podrá utilizar proveedores tecnológicos, servicios cloud, redes de distribución, servicios de correo, almacenamiento, monitoreo, seguridad, inteligencia artificial, analítica, autenticación, backups u otros servicios necesarios para prestar, mantener y mejorar el Sistema.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology implementará medidas razonables de aislamiento lógico entre organizaciones, cuentas, datos y espacios de trabajo dentro de la infraestructura multicliente. El Usuario no deberá intentar acceder, consultar, inferir, extraer, modificar o interferir con datos, cuentas, espacios o recursos de otras organizaciones o usuarios.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology podrá realizar mantenimiento programado o de emergencia, aplicar parches, actualizar componentes, cambiar infraestructura, modificar configuraciones técnicas, rotar claves, bloquear tráfico malicioso, limitar cargas anómalas o suspender accesos cuando sea razonablemente necesario para seguridad, estabilidad, cumplimiento o continuidad del servicio.',
    },
    { kind: 'heading', text: '5. Cuentas, credenciales y seguridad del Usuario' },
    {
      kind: 'paragraph',
      text: 'Cada Usuario es responsable de proteger sus credenciales, dispositivos, sesiones, tokens, factores de autenticación y medios de acceso al Sistema.',
    },
    { kind: 'paragraph', text: 'El Usuario se obliga a:' },
    {
      kind: 'bullets',
      items: [
        'usar únicamente su propia Cuenta autorizada;',
        'mantener confidenciales sus credenciales;',
        'no compartir contraseñas, enlaces de acceso, tokens, códigos de verificación ni sesiones;',
        'usar autenticación multifactor cuando esté disponible o sea requerida;',
        'cerrar sesión en dispositivos compartidos o no confiables;',
        'notificar oportunamente accesos sospechosos, pérdida de credenciales, errores graves o incidentes de seguridad;',
        'cumplir las políticas internas de la Organización y las instrucciones razonables de Cryptology relacionadas con seguridad.',
      ],
    },
    {
      kind: 'paragraph',
      text: 'Cryptology podrá suspender o limitar una Cuenta cuando existan indicios razonables de uso no autorizado, compromiso de credenciales, actividad anómala, abuso, intento de evasión, riesgo de seguridad o incumplimiento de estos Términos.',
    },
    {
      kind: 'heading',
      text: '6. Propiedad intelectual y titularidad del Sistema',
    },
    {
      kind: 'paragraph',
      text: 'El Sistema es software propietario de Cryptology o se encuentra bajo derechos que permiten a Cryptology ponerlo a disposición de los Usuarios y Organizaciones.',
    },
    {
      kind: 'paragraph',
      text: 'El acceso o uso del Sistema no constituye venta, cesión, transferencia, copropiedad, encargo de obra, adquisición de derechos patrimoniales ni entrega de propiedad intelectual a favor del Usuario o de la Organización.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology conserva todos los derechos, títulos e intereses sobre el Sistema, incluyendo, sin limitación:',
    },
    {
      kind: 'bullets',
      items: [
        'código fuente, código objeto, ejecutables, binarios, paquetes, contenedores y compilaciones;',
        'arquitectura, diseño, modelos de datos, interfaces, flujos, módulos, APIs y lógica de negocio;',
        'librerías, conectores, herramientas internas, documentación técnica, plantillas y componentes reutilizables;',
        'automatizaciones, prompts, agentes, reglas de orquestación, configuraciones internas y know-how;',
        'mejoras, actualizaciones, parches, correcciones, derivados, metodologías y secretos empresariales;',
        'marcas, nombres comerciales, derechos de autor y demás derechos de propiedad intelectual relacionados.',
      ],
    },
    {
      kind: 'paragraph',
      text: 'Los Datos de la Organización pertenecen a la Organización o a sus respectivos titulares, según corresponda. Cryptology no adquiere propiedad sobre dichos datos por el hecho de alojarlos, procesarlos o prestar soporte sobre el Sistema.',
    },
    {
      kind: 'heading',
      text: '7. No entrega de código fuente ni materiales internos',
    },
    {
      kind: 'paragraph',
      text: 'El Sistema se ofrece únicamente como servicio en línea. El Usuario no recibe ni tendrá derecho a exigir Código Fuente, repositorios, ejecutables, contenedores, imágenes, librerías privadas, scripts de construcción, pipelines, source maps, claves de firma, credenciales, documentación técnica interna, prompts, agentes, configuraciones privadas, diagramas internos o herramientas de licenciamiento.',
    },
    {
      kind: 'paragraph',
      text: 'La documentación funcional, los manuales de usuario, las pantallas del Sistema, los reportes, las respuestas de soporte o las instrucciones operativas no se interpretarán como entrega de Código Fuente ni como transferencia de propiedad intelectual.',
    },
    { kind: 'heading', text: '8. Restricciones de uso' },
    {
      kind: 'paragraph',
      text: 'El Usuario no podrá, directa ni indirectamente, por sí mismo ni mediante terceros:',
    },
    {
      kind: 'bullets',
      items: [
        'vender, revender, alquilar, arrendar, distribuir, publicar, transferir, ceder, compartir o poner el Sistema a disposición de terceros no autorizados;',
        'ofrecer el Sistema como SaaS propio, plataforma para terceros, servicio administrado, servicio de buró, outsourcing, producto empaquetado, marca blanca, white-label o solución revendida;',
        'autorizar a terceros a usar el Sistema fuera del alcance de la Organización que otorgó acceso;',
        'modificar, adaptar, traducir, alterar, transformar, extender, reconstruir, crear forks, versiones alternativas o derivados del Sistema;',
        'usar el Sistema, su comportamiento, interfaces, estructura, reportes, flujos, documentación, prompts o agentes para crear productos o servicios competidores;',
        'realizar ingeniería inversa, descompilar, desensamblar, decodificar, desencriptar, extraer, inspeccionar internamente o intentar obtener código fuente, algoritmos, arquitectura, modelos, prompts, agentes o ideas subyacentes;',
        'eliminar, ocultar o alterar avisos de propiedad, marcas, textos legales, identificadores, metadatos, referencias internas o señales de titularidad de Cryptology;',
        'evadir, alterar, desactivar o interferir con controles de acceso, autenticación, seguridad, límites de uso, validaciones, protecciones, rate limits, mecanismos de auditoría o restricciones técnicas;',
        'acceder, consultar, extraer, inferir, modificar o intentar obtener datos, cuentas, registros, recursos, espacios de trabajo o información de otras organizaciones o usuarios;',
        'introducir malware, scripts maliciosos, scraping abusivo, ataques automatizados, pruebas de estrés no autorizadas, escaneos de vulnerabilidades no coordinados o cargas que afecten el rendimiento del Sistema;',
        'usar el Sistema para actividades ilegales, fraudulentas, difamatorias, discriminatorias, engañosas, invasivas de la privacidad o que vulneren derechos de terceros;',
        'publicar vulnerabilidades, pruebas de rendimiento, análisis comparativos o información técnica no pública del Sistema sin autorización previa de Cryptology, salvo obligación legal.',
      ],
    },
    { kind: 'heading', text: '9. Marca, apariencia y presentación del Sistema' },
    {
      kind: 'paragraph',
      text: 'El Sistema puede mostrar nombres, logos, colores, textos, dominios, plantillas o elementos visuales de una Organización cuando ello haya sido configurado para facilitar sus operaciones.',
    },
    {
      kind: 'paragraph',
      text: 'Dicha personalización visual no implica que el Usuario o la Organización sean propietarios del Sistema, ni autoriza su reventa, distribución, marca blanca, white-label, uso como plataforma propia para terceros o creación de productos derivados.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology podrá mantener avisos legales, referencias técnicas, metadatos, textos de propiedad o identificadores internos necesarios para soporte, seguridad, cumplimiento o preservación de derechos.',
    },
    {
      kind: 'heading',
      text: '10. Titularidad de datos de la Organización, leads y cumplimiento legal',
    },
    {
      kind: 'paragraph',
      text: 'La Organización es y seguirá siendo titular de sus Datos de la Organización. Esto incluye, sin limitación, leads, prospectos, clientes, contactos, oportunidades comerciales, historial de comunicaciones, cotizaciones, archivos, reportes, tareas, notas, configuraciones y demás información de negocio ingresada, generada o procesada mediante el Sistema por sus Usuarios.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology no adquiere propiedad sobre los Datos de la Organización. Salvo autorización expresa, obligación legal o necesidad técnica vinculada a la prestación del Sistema, Cryptology no venderá, arrendará, cederá, sublicenciará ni explotará los Datos de la Organización como bases de datos de terceros ni los utilizará para fines incompatibles con la prestación, seguridad, soporte, mantenimiento o mejora técnica del Sistema.',
    },
    {
      kind: 'paragraph',
      text: 'Cuando los Datos de la Organización incluyan Datos Personales, la Organización será responsable de definir la finalidad, base de legitimación, consentimientos, avisos de privacidad, conservación, exactitud, atención de derechos de los titulares, transferencias, encargos, medidas internas y demás obligaciones que correspondan conforme a la Ley N.º 29733, su Reglamento y cualquier otra normativa aplicable.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology actuará como proveedor tecnológico y, cuando corresponda, como encargado del tratamiento respecto de los Datos Personales que procese por cuenta de la Organización para prestar el Sistema, salvo que un acuerdo escrito establezca expresamente un rol distinto.',
    },
    {
      kind: 'paragraph',
      text: 'El Usuario es responsable por la exactitud, legalidad, pertinencia, autorización y calidad de la información que ingrese, cargue, edite, elimine o procese mediante el Sistema. El Usuario no debe cargar datos que no esté autorizado a tratar ni utilizar el Sistema para finalidades ajenas a la Organización o contrarias a la ley.',
    },
    {
      kind: 'paragraph',
      text: 'La Organización es responsable por sus políticas internas, autorizaciones, permisos de usuarios, criterios de retención, instrucciones de tratamiento, cumplimiento regulatorio sectorial y atención de reclamos o solicitudes de titulares de datos. Cryptology podrá prestar asistencia razonable dentro del alcance técnico del Sistema y de los acuerdos comerciales aplicables.',
    },
    {
      kind: 'heading',
      text: '11. Seguridad, infraestructura, privacidad, logs y brechas',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology operará el Sistema en infraestructura SaaS multicliente administrada por Cryptology o por proveedores tecnológicos seleccionados por Cryptology. Cryptology implementará medidas técnicas y organizativas razonables orientadas a proteger los Datos de la Organización contra accesos no autorizados, pérdida, alteración, destrucción, divulgación indebida o uso no autorizado.',
    },
    {
      kind: 'paragraph',
      text: 'Dichas medidas podrán incluir, según corresponda y de forma razonable para la naturaleza del servicio: separación lógica entre organizaciones, controles de acceso, autenticación, gestión de permisos, cifrado en tránsito, controles de infraestructura, monitoreo, registros técnicos, copias de respaldo, revisión de eventos, parches, actualizaciones de seguridad, rotación de claves, limitación de privilegios y respuesta ante incidentes.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology realizará esfuerzos comercialmente razonables para mantener una infraestructura segura y reducir el riesgo de brechas. No obstante, ningún sistema conectado a internet, servicio cloud, software, credencial, integración o infraestructura tecnológica puede garantizar seguridad absoluta, invulnerabilidad total o ausencia completa de incidentes.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology no será responsable por Incidentes de Seguridad, pérdida, exposición o alteración de datos causados por credenciales comprometidas, contraseñas débiles, sesiones compartidas, dispositivos inseguros, phishing, errores del Usuario o de la Organización, configuraciones solicitadas por la Organización, integraciones de terceros, instrucciones incorrectas, importaciones defectuosas, uso no autorizado, malware externo, redes del Usuario, proveedores fuera del control razonable de Cryptology o incumplimiento de políticas de seguridad por parte de la Organización o sus Usuarios.',
    },
    {
      kind: 'paragraph',
      text: 'En caso de un Incidente de Seguridad confirmado que afecte materialmente Datos de la Organización o Datos Personales procesados mediante el Sistema, Cryptology realizará esfuerzos razonables para investigar, contener, mitigar y remediar el incidente, y comunicará la información razonablemente disponible a la Organización cuando corresponda, de acuerdo con la normativa aplicable y los acuerdos comerciales vigentes.',
    },
    {
      kind: 'paragraph',
      text: 'Para operar, proteger, mantener, auditar y mejorar el Sistema, Cryptology podrá registrar y procesar información técnica y operativa relacionada con el uso del servicio, incluyendo identificadores de usuario, organización, dirección IP, fecha y hora de acceso, navegador, dispositivo, acciones realizadas, errores, eventos de seguridad, métricas de rendimiento y registros de soporte.',
    },
    {
      kind: 'paragraph',
      text: 'Dicha información podrá utilizarse para autenticación, seguridad, prevención de abuso, investigación de incidentes, corrección de errores, cumplimiento contractual, cumplimiento legal, auditoría, mejora del servicio y soporte técnico.',
    },
    { kind: 'heading', text: '12. Soporte correctivo y corrección de errores' },
    {
      kind: 'paragraph',
      text: 'Cryptology se encargará de realizar soporte correctivo sobre errores técnicos reproducibles imputables al Sistema durante la vigencia del servicio aplicable a la Organización.',
    },
    {
      kind: 'paragraph',
      text: 'El soporte correctivo puede incluir análisis de reportes, reproducción de fallas, revisión de logs, entrega de parches, actualizaciones correctivas, configuraciones alternativas, correcciones de seguridad, workarounds razonables y despliegue de mejoras técnicas necesarias.',
    },
    {
      kind: 'paragraph',
      text: 'Para reportar un error, el Usuario deberá proporcionar información razonable, incluyendo descripción del problema, pasos para reproducirlo, módulo afectado, fecha y hora aproximada, capturas, mensajes de error, impacto operativo y cambios recientes conocidos.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology podrá priorizar los reportes según severidad, impacto, reproducibilidad, número de usuarios afectados, riesgo de seguridad, disponibilidad de workaround y condiciones comerciales aplicables.',
    },
    {
      kind: 'paragraph',
      text: 'El soporte correctivo no incluye, salvo acuerdo comercial expreso, nuevas funcionalidades, rediseños, cambios de alcance, integraciones nuevas, capacitación adicional, soporte a dispositivos del Usuario, problemas de conectividad local, errores causados por datos incorrectos, usos no autorizados, automatizaciones externas, navegadores obsoletos o servicios de terceros fuera del control de Cryptology.',
    },
    {
      kind: 'heading',
      text: '13. Actualizaciones, cambios y continuidad del servicio',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology podrá actualizar, modificar, optimizar, reemplazar, retirar, combinar o mejorar funcionalidades del Sistema para mantener seguridad, estabilidad, rendimiento, compatibilidad, cumplimiento normativo, continuidad operativa o evolución del producto.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology procurará evitar interrupciones innecesarias, pero el Usuario reconoce que el Sistema puede estar sujeto a mantenimiento programado, mantenimiento de emergencia, degradaciones temporales, limitaciones de proveedores, incidentes de seguridad, actualizaciones críticas o causas fuera del control razonable de Cryptology.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology no está obligada a mantener indefinidamente versiones antiguas, interfaces obsoletas, APIs descontinuadas, configuraciones inseguras o funcionalidades reemplazadas.',
    },
    { kind: 'heading', text: '14. Integraciones, terceros e inteligencia artificial' },
    {
      kind: 'paragraph',
      text: 'El Sistema puede integrarse con servicios de terceros, incluyendo proveedores cloud, correo electrónico, mensajería, almacenamiento, autenticación, pagos, analítica, inteligencia artificial, bases de datos, APIs externas u otros servicios conectados por Cryptology o por la Organización.',
    },
    {
      kind: 'paragraph',
      text: 'La disponibilidad, seguridad, precisión, continuidad y condiciones de dichos servicios pueden depender de sus respectivos proveedores y términos. Cryptology no será responsable por fallas, cambios, suspensiones, errores, costos o limitaciones de servicios de terceros fuera de su control razonable.',
    },
    {
      kind: 'paragraph',
      text: 'Cuando existan funcionalidades asistidas por inteligencia artificial, el Usuario deberá revisar los resultados antes de tomar decisiones, enviar comunicaciones, modificar datos relevantes o ejecutar acciones con impacto legal, financiero, comercial, operativo o reputacional. Las respuestas generadas o asistidas por IA pueden contener errores, omisiones o inferencias no verificadas.',
    },
    { kind: 'heading', text: '15. Suspensión o limitación de acceso' },
    {
      kind: 'paragraph',
      text: 'Cryptology podrá suspender, limitar o bloquear el acceso de un Usuario o de una Organización, total o parcialmente, cuando exista cualquiera de las siguientes situaciones:',
    },
    {
      kind: 'bullets',
      items: [
        'incumplimiento de estos Términos;',
        'uso no autorizado, abusivo, fraudulento, ilegal o riesgoso;',
        'intento de acceder a datos o recursos de terceros;',
        'riesgo de seguridad, credenciales comprometidas o actividad anómala;',
        'requerimiento legal, orden de autoridad competente o necesidad de preservar evidencia;',
        'falta de pago o vencimiento del servicio de la Organización;',
        'mantenimiento urgente, incidente crítico o protección de la infraestructura multicliente;',
        'infracción de derechos de propiedad intelectual, confidencialidad o restricciones de uso.',
      ],
    },
    {
      kind: 'paragraph',
      text: 'La suspensión podrá mantenerse mientras exista el riesgo, incumplimiento, investigación, deuda, incidente, requerimiento o causa que la motivó.',
    },
    { kind: 'heading', text: '16. Terminación y efectos' },
    {
      kind: 'paragraph',
      text: 'El Usuario puede dejar de usar el Sistema en cualquier momento. La Organización puede solicitar altas, bajas, modificaciones de permisos o eliminación de cuentas conforme a sus procesos internos y acuerdos aplicables.',
    },
    {
      kind: 'paragraph',
      text: 'Al terminar el acceso, el Usuario deberá cesar el uso del Sistema, no intentar ingresar mediante credenciales ajenas, no conservar información no autorizada y no usar datos, reportes o información obtenida del Sistema para fines indebidos.',
    },
    {
      kind: 'paragraph',
      text: 'Las obligaciones relacionadas con propiedad intelectual, restricciones de uso, confidencialidad, seguridad, responsabilidad, protección de datos y uso de información sobrevivirán al cierre de la Cuenta o terminación del acceso.',
    },
    {
      kind: 'paragraph',
      text: 'La exportación, retención, eliminación o devolución de Datos de la Organización se regirá por los acuerdos aplicables entre Cryptology y la Organización, por las instrucciones autorizadas de la Organización y por la normativa aplicable.',
    },
    { kind: 'heading', text: '17. Confidencialidad' },
    {
      kind: 'paragraph',
      text: 'El Usuario debe mantener en reserva toda información confidencial o no pública a la que acceda mediante el Sistema, incluyendo información de la Organización, clientes finales, operaciones, credenciales, reportes, configuraciones, documentación, precios, procesos, datos personales y datos comerciales.',
    },
    {
      kind: 'paragraph',
      text: 'También se considera información confidencial de Cryptology la información técnica no pública del Sistema, incluyendo arquitectura, diseño, comportamiento interno, seguridad, código, estructura, integraciones, prompts, agentes, flujos, documentación técnica, reportes de vulnerabilidad, métodos, herramientas, know-how y planes de producto.',
    },
    {
      kind: 'paragraph',
      text: 'El Usuario no podrá divulgar, copiar, exportar, publicar, capturar, grabar, analizar o utilizar información confidencial para fines no autorizados, salvo autorización expresa o obligación legal aplicable.',
    },
    {
      kind: 'heading',
      text: '18. Garantía limitada y esfuerzos razonables de seguridad',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology realizará esfuerzos comercialmente razonables para mantener el Sistema operativo, seguro y funcional, y para corregir errores técnicos reproducibles imputables al Sistema conforme a estos Términos y a los acuerdos aplicables.',
    },
    {
      kind: 'paragraph',
      text: 'Cryptology también realizará esfuerzos razonables para proteger los Datos de la Organización mediante medidas técnicas y organizativas acordes con la naturaleza del servicio, los riesgos razonablemente previsibles, la infraestructura disponible y las condiciones comerciales aplicables.',
    },
    {
      kind: 'paragraph',
      text: 'Salvo lo expresamente pactado, el Sistema se proporciona “tal cual” y “según disponibilidad”. Cryptology no garantiza que el Sistema será ininterrumpido, libre de errores, inmune a ataques, invulnerable, compatible con todos los dispositivos, navegadores o integraciones, ni adecuado para toda finalidad particular del Usuario o de la Organización.',
    },
    {
      kind: 'paragraph',
      text: 'El Usuario reconoce que los resultados, seguridad y disponibilidad del Sistema también dependen de datos ingresados, configuraciones, permisos, integraciones, servicios de terceros, conectividad, dispositivos, credenciales, decisiones operativas de la Organización y uso correcto de la plataforma.',
    },
    { kind: 'heading', text: '19. Limitación de responsabilidad' },
    {
      kind: 'paragraph',
      text: 'En la máxima medida permitida por la ley aplicable, Cryptology no será responsable frente al Usuario por daños indirectos, lucro cesante, pérdida de oportunidad, pérdida de ingresos, pérdida de reputación, pérdida de datos causada por acciones del Usuario o de la Organización, decisiones tomadas con información no verificada, interrupciones de terceros, fallas de conectividad, usos no autorizados, incidentes originados fuera del control razonable de Cryptology o incumplimientos de estos Términos.',
    },
    {
      kind: 'paragraph',
      text: 'Respecto de Incidentes de Seguridad, brechas, pérdida, alteración o exposición de datos, la responsabilidad de Cryptology quedará limitada, en la máxima medida permitida por la ley, a los daños directos efectivamente comprobados que sean consecuencia directa de un incumplimiento imputable a Cryptology de sus obligaciones contractuales de seguridad. No se asumirá responsabilidad por causas atribuibles al Usuario, la Organización, terceros, proveedores fuera del control razonable de Cryptology, fuerza mayor, caso fortuito o configuraciones/instrucciones solicitadas por la Organización.',
    },
    {
      kind: 'paragraph',
      text: 'Cuando el Usuario actúe por cuenta de una Organización, la responsabilidad comercial de Cryptology frente a dicha Organización se regirá además por los acuerdos comerciales vigentes entre Cryptology y la Organización, incluyendo cualquier límite de responsabilidad, exclusión de daños indirectos, SLA, anexo de tratamiento de datos, política de seguridad o condiciones de soporte aplicables.',
    },
    {
      kind: 'paragraph',
      text: 'Nada en estos Términos excluye o limita responsabilidades, obligaciones de notificación o derechos que no puedan ser excluidos o limitados conforme a la ley aplicable.',
    },
    { kind: 'heading', text: '20. Cambios a estos Términos' },
    {
      kind: 'paragraph',
      text: 'Cryptology podrá actualizar estos Términos para reflejar cambios del Sistema, mejoras operativas, requisitos legales, condiciones de seguridad, prácticas comerciales o ajustes de servicio.',
    },
    {
      kind: 'paragraph',
      text: 'La versión vigente podrá publicarse dentro del Sistema, en el login, en una sección legal, mediante enlace, notificación o documento equivalente. El uso continuado del Sistema después de la publicación o notificación de cambios constituye aceptación de la versión vigente, salvo que la ley aplicable requiera una aceptación distinta.',
    },
    { kind: 'heading', text: '21. Ley aplicable y solución de controversias' },
    {
      kind: 'paragraph',
      text: 'Estos Términos se rigen por las leyes de la República del Perú.',
    },
    {
      kind: 'paragraph',
      text: 'Cualquier controversia relacionada con el uso del Sistema se procurará resolver primero mediante comunicación directa con Cryptology o mediante los canales establecidos por la Organización. Si la controversia involucra obligaciones comerciales de la Organización, se aplicarán también los mecanismos de solución de controversias pactados entre Cryptology y dicha Organización.',
    },
    {
      kind: 'paragraph',
      text: 'En ausencia de un mecanismo contractual específico aplicable, las partes se someten a la competencia de los jueces y tribunales competentes de Lima, Perú, salvo norma imperativa en contrario.',
    },
    { kind: 'heading', text: '22. Contacto' },
    {
      kind: 'paragraph',
      text: 'Para consultas sobre estos Términos, soporte correctivo, seguridad, reportes de errores o comunicaciones relacionadas con el Sistema, el Usuario o la Organización pueden contactar a Cryptology en hola@inmueble.red.',
    },
    { kind: 'heading', text: '23. Disposición final' },
    {
      kind: 'paragraph',
      text: 'Estos Términos constituyen las condiciones generales de uso del Sistema para Usuarios autorizados. Cualquier condición comercial, alcance de módulos, precios, niveles de soporte, responsabilidades específicas, tratamiento de datos, plazos, retención de información o servicio administrado aplicable a una Organización se regulará por los acuerdos comerciales, anexos, propuestas, políticas o documentos suscritos o aceptados entre Cryptology y dicha Organización.',
    },
    { kind: 'subheading', text: 'Control de versión' },
    {
      kind: 'table',
      rows: [
        ['Versión', 'Fecha de vigencia', 'Descripción'],
        ...release.versionHistory,
      ],
    },
  ],
});

const COMMERCIAL_TERMS_V1_1_HISTORY_ROW = [
  '1.1',
  '9 de junio de 2026',
  'Versión general para usuarios autorizados del Sistema SaaS multicliente, con cláusulas reforzadas sobre titularidad de datos de la Organización, cumplimiento de protección de datos personales, seguridad razonable, brechas y limitación de responsabilidad.',
] as const;

// Release 1.1 stays published as history. The legal manifest pins it by
// checksum, so its text and the domicile published at the time never change.
export const commercialTermsOfUseDocumentV1_1 =
  buildCommercialTermsOfUseDocument({
    effectiveVersion: 'Versión 1.1 | Vigente desde el 9 de junio de 2026',
    publishedAddress: 'Malecón Cisneros 428, Miraflores, 15074, Lima, Perú',
    versionHistory: [COMMERCIAL_TERMS_V1_1_HISTORY_ROW],
  });

export const commercialTermsOfUseDocument = buildCommercialTermsOfUseDocument({
  effectiveVersion: COMMERCIAL_TERMS_EFFECTIVE_LABEL,
  publishedAddress:
    'Calle Conde de la Monclova 378, 15073, San Isidro, Lima, Perú',
  versionHistory: [
    COMMERCIAL_TERMS_V1_1_HISTORY_ROW,
    [
      '1.2',
      '7 de octubre de 2026',
      'Actualización del domicilio publicado del proveedor.',
    ],
  ],
});
